import { CashToClose } from "@eave/shared/types/api";
import React, { useState, useEffect } from "react";
import LabeledObject from "web_modules/shared/LabeledObject";
import { formatCurrency } from "@eave/shared/utils/currency";
import Section from "web_modules/shared/Section";
import { fetchCashToCloseSummary } from "./api-helpers";

const styles = require("./styles.module.scss");

interface Props {
  appId: number;
  setCashToCloseValues: (ctc: CashToClose) => void;
}

const summaryDisplayText = {
  total_closing_costs: "Total Closing Costs",
  closing_costs_financed: "Closing Costs Financed",
  closing_costs_paid_before_closing: "Closing Costs Paid Before Closing",
  down_payment: "Down Payment",
  deposit: "Deposit",
  funds_for_borrower: "Funds for Borrower",
  seller_credits: "Seller Credits",
  adjustments_and_other_credits: "Adjustments and Other Credits",
};

/**
 * Computes the total cash to close by just adding up all the items on the cashToClose object
 * Note: negative assets will be parsed as negative amounts, so a simple summation is all that's needed.
 *
 * @param {CashToClose} cashToClose - the cash to close object
 * @returns
 */
export const computeTotalCashToClose = (cashToClose: CashToClose): number =>
  Object.keys(cashToClose).reduce((acc, key) => acc + parseFloat(cashToClose[key]), 0);

const CashToCloseAssetSummary: React.FC<Props> = ({ appId, setCashToCloseValues }: Props) => {
  const [cashToClose, setCashToClose] = useState<CashToClose>(null);

  useEffect(() => {
    fetchCashToCloseSummary(appId)
      .then(({ cash_to_close }) => {
        setCashToClose(cash_to_close);
      })
      .catch(({ meta: { error_detail } }) => {
        throw new Error(error_detail);
      });
  }, []);

  useEffect(() => {
    if (cashToClose) {
      setCashToCloseValues(cashToClose);
    }
  }, [cashToClose]);

  const listData = cashToClose
    ? Object.keys(cashToClose).map(key => ({
        label: summaryDisplayText[key],
        value: cashToClose[key] ? formatCurrency(cashToClose[key]) : "",
      }))
    : [];

  const cashToCloseTotal = cashToClose ? computeTotalCashToClose(cashToClose) : 0;
  const title = cashToClose ? `Cash to Close: ${formatCurrency(cashToCloseTotal, 2, 2)}` : "-";

  return (
    <Section heading={title}>
      <div className={styles.cashToCloseAssetSummary}>
        <LabeledObject
          title=""
          listData={listData.slice(0, 4)}
          size="compact"
          kind="minimal-half-width"
        />
        <LabeledObject
          title=""
          listData={listData.slice(4)}
          kind="minimal-half-width"
          size="compact"
        />
      </div>
    </Section>
  );
};

export default CashToCloseAssetSummary;
